import { useRef } from 'react';

import { useAuth } from '../../providers/AuthProvider';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';

/**
 * Blocking notice shown once when the session expires.
 *
 * Mounted once at the app root, so every page gets it without its own handling.
 * Not dismissible: the only way forward is to sign in again, and a dialog that can
 * be closed would leave the operator on a screen whose every action now fails.
 *
 * The page behind it stays mounted and unchanged — the dialog does not clear
 * anything itself. Clearing happens only when "Log In Again" is chosen, and even
 * then the campaign draft in this tab is kept for the same operator.
 */
export function SessionExpiredModal() {
  const { sessionExpired, reauthenticate, isReauthenticating } = useAuth();
  const buttonRef = useRef(null);

  return (
    <Modal
      open={sessionExpired}
      onClose={() => {}}
      dismissible={false}
      closeOnBackdrop={false}
      role="alertdialog"
      size="sm"
      title="Your session has expired"
      titleIcon="fa-lock"
      titleTone="warning"
      initialFocusRef={buttonRef}
      footer={
        <Button
          ref={buttonRef}
          variant="primary"
          icon="fa-right-to-bracket"
          onClick={reauthenticate}
          loading={isReauthenticating}
          loadingLabel="Signing out…"
        >
          Log In Again
        </Button>
      }
    >
      <p className="text-base text-ink-700">
        For your security, your session has expired. Please log in again to continue.
      </p>
      <p className="mt-3 text-sm text-muted">
        Your campaign draft in this tab is kept and will be restored when you sign back in with the
        same account. Emails already queued keep sending in the meantime.
      </p>
    </Modal>
  );
}
